/**
 * Nook M6-4.1 · Friend-side password reset completion at `/reset-password/:token`.
 *
 * This page replaces the M6-4 placeholder stub. The friend lands here via the
 * reset URL generated by the Owner via `<PasswordResetCard>`. The page:
 *
 *   1. Reads `:token` from the URL params.
 *   2. Shows a form with New password + Confirm password fields.
 *   3. Client-side validates: passwords match, min 8 chars.
 *   4. POSTs to the anonymous EF `reset-password-complete` (verify_jwt=false).
 *   5. On success: displays a success card with a "Log in" button.
 *   6. On error: displays an error strip with i18n-mapped messages.
 *
 * The page is intentionally UNGUARDED (no RequireAuth) — the friend may not
 * have a valid session (they might be resetting because they forgot their
 * password). The token itself is the auth credential.
 *
 * SPEC:
 * - F-AUTH-07 / AC.16: Token-based one-time password reset.
 * - M6-4.1 (deferred from M6-4): Single-session increment.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { adminApi } from '@/lib/api/admin';

// ========================================================================
// State machine
// ========================================================================

type PageState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; code: string; message: string }
  | { kind: 'success' };

// ========================================================================
// Error code → i18n key mapper
// ========================================================================

function codeToI18nKey(code: string): string {
  switch (code) {
    case 'E_VAL_INVALID_FORMAT':
      return 'resetComplete.errors.invalidInput';
    case 'E_RES_NOT_FOUND':
      return 'resetComplete.errors.tokenNotFound';
    case 'E_RES_TOKEN_EXPIRED':
      return 'resetComplete.errors.tokenExpired';
    case 'E_RES_TOKEN_USED':
      return 'resetComplete.errors.tokenUsed';
    case 'E_RES_TOKEN_REVOKED':
      return 'resetComplete.errors.tokenRevoked';
    case 'INTERNAL':
      return 'resetComplete.errors.internal';
    default:
      return 'resetComplete.errors.unknown';
  }
}

// ========================================================================
// Component
// ========================================================================

export default function ResetPasswordPlaceholderPage() {
  const { t } = useTranslation();
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [state, setState] = useState<PageState>({ kind: 'idle' });
  const [validationError, setValidationError] = useState<string | null>(null);

  // ── Derived state ──────────────────────────────────────────────
  const isValidToken = /^[A-Za-z0-9_-]{32}$/.test(token);

  // ── Validation helpers ─────────────────────────────────────────
  const validate = (): string | null => {
    if (password.length < 8) {
      return t('errors.passwordTooShort');
    }
    if (password !== confirmPassword) {
      return t('auth.passwordMismatch');
    }
    return null;
  };

  // ── Submit handler ──────────────────────────────────────────────
  const handleSubmit = async () => {
    // Client-side validation first
    const vErr = validate();
    if (vErr) {
      setValidationError(vErr);
      return;
    }
    setValidationError(null);

    setState({ kind: 'submitting' });

    try {
      const result = await adminApi.resetPasswordComplete({ token, password });
      if (result.success) {
        setState({ kind: 'success' });
      } else {
        setState({
          kind: 'error',
          code: 'INTERNAL',
          message: result.message || t('resetComplete.errors.internal'),
        });
      }
    } catch (err) {
      const m = err as { code?: string; message?: string };
      const code = m?.code ?? 'INTERNAL';
      setState({
        kind: 'error',
        code,
        message: m?.message ?? t('resetComplete.errors.internal'),
      });
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <main
      className="
        min-h-screen flex flex-col items-center justify-center
        bg-[var(--color-canvas-default)]
        p-[var(--space-xl)]
      "
      data-testid="reset-password-page"
    >
      <div
        className="
          flex flex-col items-center gap-[var(--space-lg)]
          max-w-[480px] w-full
        "
      >
        {/* ── Logo ───────────────────────────────────────────── */}
        <div className="flex flex-col items-center gap-[var(--space-sm)] text-center">
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            aria-hidden="true"
            className="text-[var(--color-ink-muted)]"
          >
            <rect
              x="9"
              y="20"
              width="30"
              height="20"
              rx="3"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M16 20v-4a8 8 0 0 1 16 0v4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="24" cy="30" r="2" fill="currentColor" />
          </svg>
          <h1 className="text-[var(--font-size-h2)] font-[600] text-[var(--color-ink-default)]">
            {t('resetComplete.title')}
          </h1>
          <p className="text-[var(--font-size-body-md)] text-[var(--color-ink-muted)] leading-[var(--leading-chill)] max-w-[360px]">
            {t('resetComplete.subtitle')}
          </p>
        </div>

        {/* ── Invalid token ──────────────────────────────────── */}
        {!isValidToken && (
          <div
            className="
              w-full px-[var(--space-md)] py-[var(--space-lg)]
              flex flex-col items-center gap-[var(--space-md)] text-center
              rounded-[var(--radius-xl)] border border-[var(--color-hairline-default)]
              bg-[var(--color-surface-1)]
            "
            data-testid="reset-password-invalid-token"
          >
            <p className="text-[var(--font-size-body-md)] text-[var(--color-signal-error)]">
              {t('resetComplete.errors.invalidToken')}
            </p>
            <Link to="/welcome">
              <Button intent="accent" size="md">
                {t('resetPlaceholder.home')}
              </Button>
            </Link>
          </div>
        )}

        {/* ── Valid token: show form or success state ────────── */}
        {isValidToken && state.kind !== 'success' && (
          <div
            className="
              w-full flex flex-col gap-[var(--space-md)]
              rounded-[var(--radius-xl)] border border-[var(--color-hairline-default)]
              bg-[var(--color-surface-1)]
              p-[var(--space-lg)]
            "
            data-testid="reset-password-form"
          >
            {/* Token display (informational) */}
            <div
              className="
                px-[var(--space-md)] py-[var(--space-sm)]
                bg-[var(--color-surface-2)] border border-[var(--color-hairline-default)]
                rounded-[var(--radius-md)]
              "
              data-testid="reset-password-token-display"
            >
              <span className="block text-[var(--font-size-meta)] text-[var(--color-ink-muted)] mb-[var(--space-2xs)]">
                {t('resetPlaceholder.tokenLabel')}
              </span>
              <code className="block text-[var(--font-size-meta)] text-[var(--color-ink-default)] break-all">
                {token.slice(0, 16)}…{token.slice(-8)}
              </code>
            </div>

            {/* New password field */}
            <div className="flex flex-col gap-[var(--space-sm)]">
              <label
                htmlFor="reset-password-new-password"
                className="text-[var(--font-size-meta)] text-[var(--color-ink-muted)]"
              >
                {t('resetComplete.newPassword')}
              </label>
              <input
                id="reset-password-new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={state.kind === 'submitting'}
                className="
                  w-full px-[var(--space-md)] py-[var(--space-sm)]
                  bg-[var(--color-surface-2)] border border-[var(--color-hairline-default)]
                  rounded-[var(--radius-md)]
                  text-[var(--font-size-body-md)] text-[var(--color-ink-default)]
                  placeholder:text-[var(--color-ink-muted)]
                  focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]
                  disabled:opacity-50
                "
                placeholder={t('resetComplete.passwordPlaceholder')}
                data-testid="reset-password-new-password"
              />
            </div>

            {/* Confirm password field */}
            <div className="flex flex-col gap-[var(--space-sm)]">
              <label
                htmlFor="reset-password-confirm-password"
                className="text-[var(--font-size-meta)] text-[var(--color-ink-muted)]"
              >
                {t('resetComplete.confirmPassword')}
              </label>
              <input
                id="reset-password-confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={state.kind === 'submitting'}
                className="
                  w-full px-[var(--space-md)] py-[var(--space-sm)]
                  bg-[var(--color-surface-2)] border border-[var(--color-hairline-default)]
                  rounded-[var(--radius-md)]
                  text-[var(--font-size-body-md)] text-[var(--color-ink-default)]
                  placeholder:text-[var(--color-ink-muted)]
                  focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]
                  disabled:opacity-50
                "
                placeholder={t('auth.confirmPassword')}
                data-testid="reset-password-confirm-password"
              />
            </div>

            {/* Client-side validation error */}
            {validationError && (
              <p
                role="alert"
                data-testid="reset-password-validation-error"
                className="text-[var(--font-size-meta)] text-[var(--color-signal-error)]"
              >
                {validationError}
              </p>
            )}

            {/* Server-side error strip */}
            {state.kind === 'error' && (
              <p
                role="alert"
                data-testid="reset-password-server-error"
                className="
                  px-[var(--space-md)] py-[var(--space-sm)]
                  text-[var(--font-size-meta)] text-[var(--color-signal-error)]
                  bg-[var(--color-signal-error-soft)]
                  border border-[var(--color-signal-error)]
                  rounded-[var(--radius-md)]
                "
              >
                {t(codeToI18nKey(state.code))}
              </p>
            )}

            {/* Submit button */}
            <Button
              intent="accent"
              size="md"
              onClick={handleSubmit}
              disabled={
                state.kind === 'submitting' ||
                password.length === 0 ||
                confirmPassword.length === 0
              }
              loading={state.kind === 'submitting'}
              data-testid="reset-password-submit"
            >
              {state.kind === 'submitting'
                ? t('resetComplete.submitting')
                : t('resetComplete.submit')}
            </Button>
          </div>
        )}

        {/* ── Success state ──────────────────────────────────── */}
        {isValidToken && state.kind === 'success' && (
          <div
            className="
              w-full flex flex-col items-center gap-[var(--space-md)] text-center
              rounded-[var(--radius-xl)] border border-[var(--color-hairline-default)]
              bg-[var(--color-surface-1)]
              p-[var(--space-lg)]
            "
            data-testid="reset-password-success"
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              aria-hidden="true"
              className="text-[var(--color-signal-success)]"
            >
              <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="2" />
              <path
                d="M16 24l6 6 10-10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h2 className="text-[var(--font-size-h3)] font-[600] text-[var(--color-ink-default)]">
              {t('resetComplete.success.title')}
            </h2>
            <p className="text-[var(--font-size-body-md)] text-[var(--color-ink-muted)] leading-[var(--leading-chill)]">
              {t('resetComplete.success.message')}
            </p>
            <Link to="/login" className="mt-[var(--space-sm)]">
              <Button intent="accent" size="md" data-testid="reset-password-login-link">
                {t('resetComplete.success.login')}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </main>
  );
}
